import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createFrontendModule, PageBlueprint, useApi } from '@backstage/frontend-plugin-api';
import { type SearchApi, searchApiRef } from '@backstage/plugin-search-react';
import { FilterGroup, KIND_LABELS, label } from './EntityBrowser';

type SearchResultSet = Awaited<ReturnType<SearchApi['query']>>;

const SEARCH_DELAY_MS = 300;
const PAGE_LIMIT = 25;
const TEXT_LENGTH = 180;

const SOURCE_LABELS: Record<string, string> = {
  'software-catalog': 'Catalogus',
  techdocs: 'Documentatie',
};

const SOURCES = Object.keys(SOURCE_LABELS).map(value => ({ value }));
const KINDS = ['Component', 'API', 'Resource', 'Template', 'Group'].map(value => ({ value }));

const shorten = (text: string) =>
  text.length > TEXT_LENGTH ? `${text.slice(0, TEXT_LENGTH).trimEnd()}…` : text;

function SearchPage() {
  const searchApi = useApi(searchApiRef);
  const [params, setParams] = useSearchParams();
  const [results, setResults] = useState<SearchResultSet>();
  const [failed, setFailed] = useState(false);
  const field = useRef<HTMLElement>(null);
  const openFilters = useRef<HTMLElement>(null);
  const openFiltersOverflow = useRef<HTMLElement>(null);
  const section = useRef<HTMLElement & { show?: () => void }>(null);

  const term = params.get('query') ?? '';
  const source = params.get('in') ?? '';
  const kind = params.get('soort') ?? '';
  const cursor = params.get('pagina') ?? undefined;

  const update = useCallback(
    (change: (next: URLSearchParams) => void) => {
      setParams(current => {
        const next = new URLSearchParams(current);
        change(next);
        next.delete('pagina');
        return next;
      });
    },
    [setParams],
  );

  const choose = useCallback(
    (name: string, value: string) =>
      update(next => {
        if (next.get(name) === value) {
          next.delete(name);
        } else {
          next.set(name, value);
        }
        if (name === 'in' && next.get('in') === 'techdocs') {
          next.delete('soort');
        }
      }),
    [update],
  );

  useEffect(() => {
    const element = field.current;
    let timer: ReturnType<typeof setTimeout>;
    const onInput = (event: Event) => {
      const value = (event as CustomEvent<{ value?: string }>).detail?.value ?? '';
      clearTimeout(timer);
      timer = setTimeout(
        () => update(next => (value ? next.set('query', value) : next.delete('query'))),
        SEARCH_DELAY_MS,
      );
    };
    element?.addEventListener('input', onInput);
    return () => {
      clearTimeout(timer);
      element?.removeEventListener('input', onInput);
    };
  }, [update]);

  useEffect(() => {
    const button = openFilters.current;
    const item = openFiltersOverflow.current;
    const open = () => section.current?.show?.();
    button?.addEventListener('click', open);
    item?.addEventListener('select', open);
    return () => {
      button?.removeEventListener('click', open);
      item?.removeEventListener('select', open);
    };
  }, []);

  useEffect(() => {
    if (!term) {
      setResults(undefined);
      return undefined;
    }
    const request = new AbortController();
    searchApi
      .query(
        {
          term,
          types: source ? [source] : [],
          filters: kind ? { kind } : {},
          pageCursor: cursor,
          pageLimit: PAGE_LIMIT,
        },
        { signal: request.signal },
      )
      .then(found => {
        setResults(found);
        setFailed(false);
      })
      .catch(() => {
        if (!request.signal.aborted) {
          setFailed(true);
        }
      });
    return () => request.abort();
  }, [searchApi, term, source, kind, cursor]);

  const goTo = (pageCursor: string | undefined) =>
    setParams(current => {
      const next = new URLSearchParams(current);
      if (pageCursor) {
        next.set('pagina', pageCursor);
      } else {
        next.delete('pagina');
      }
      return next;
    });

  return (
    <div>
      <nldd-sidebar-section
        ref={section}
        sidebar-label="Filters"
        translations='{"components.sidebar-section.sheet-dismiss-action": "Klaar"}'
      >
        <nldd-title slot="header" size="1" heading-level="1" text="Zoeken" />
        <nldd-container slot="sidebar" padding="16" padding-bottom="8">
          <FilterGroup
            title="Zoek in"
            facets={SOURCES}
            selected={source ? [source] : []}
            labels={SOURCE_LABELS}
            onToggle={value => choose('in', value)}
          />
          {source === 'techdocs' ? null : (
            <FilterGroup
              title="Soort"
              facets={KINDS}
              selected={kind ? [kind] : []}
              labels={KIND_LABELS}
              onToggle={value => choose('soort', value)}
            />
          )}
        </nldd-container>

        <nldd-toolbar accessible-label="Zoeken en filteren">
          <nldd-toolbar-item slot="start" min-width="240px" width="100%" max-width="560px">
            <nldd-search-field ref={field} value={term} placeholder="Zoek in catalogus, registers en kennisbank" />
            <nldd-menu-item slot="overflow" icon="search" text="Zoeken" />
          </nldd-toolbar-item>
          <nldd-toolbar-item class="portal-open-filters" slot="end">
            <nldd-button ref={openFilters} appearance="secondary" start-icon="filter" text="Filters" />
            <nldd-menu-item ref={openFiltersOverflow} slot="overflow" icon="filter" text="Filters" />
          </nldd-toolbar-item>
        </nldd-toolbar>
        <nldd-spacer size="16" />

        {failed ? (
          <nldd-banner variant="critical" text="Zoeken lukt nu niet" supporting-text="Probeer het later opnieuw." />
        ) : null}
        {!failed && !term ? (
          <nldd-inline-dialog
            icon="magnifier"
            text="Typ waar je naar zoekt"
            supporting-text="Je zoekt in alles wat de overheid bouwt en publiceert, en in de documentatie daarbij."
          />
        ) : null}
        {!failed && term && results?.results.length === 0 ? (
          <nldd-inline-dialog
            icon="magnifier"
            text={`Niets gevonden voor "${term}"`}
            supporting-text="Probeer een ander woord, of haal een filter weg."
          />
        ) : null}
        {!failed && term && results?.results.length ? (
          <>
            {results.numberOfResults ? (
              <>
                <nldd-text size="sm" color="secondary" aria-live="polite">
                  {results.numberOfResults.toLocaleString('nl-NL')} resultaten
                </nldd-text>
                <nldd-spacer size="4" />
              </>
            ) : null}
            <nldd-list appearance="box-base" type="navigation" accessible-label="Zoekresultaten">
              {results.results.map(result => (
                <nldd-list-item key={result.document.location} href={result.document.location}>
                  <nldd-text-cell
                    overline={
                      result.type === 'techdocs'
                        ? SOURCE_LABELS.techdocs
                        : label(KIND_LABELS, String((result.document as { kind?: string }).kind ?? ''))
                    }
                    text={result.document.title}
                    supporting-text={shorten(result.document.text ?? '')}
                    query={term}
                    query-mark-mode="match"
                  />
                </nldd-list-item>
              ))}
            </nldd-list>
            {results.previousPageCursor || results.nextPageCursor ? (
              <>
                <nldd-spacer size="24" />
                <nldd-button-group>
                  {results.previousPageCursor ? (
                    <PageButton text="Vorige" onPress={() => goTo(results.previousPageCursor)} />
                  ) : null}
                  {results.nextPageCursor ? (
                    <PageButton text="Volgende" onPress={() => goTo(results.nextPageCursor)} />
                  ) : null}
                </nldd-button-group>
              </>
            ) : null}
          </>
        ) : null}
      </nldd-sidebar-section>
    </div>
  );
}

function PageButton(props: { text: string; onPress: () => void }) {
  const button = useRef<HTMLElement>(null);
  const { onPress } = props;
  useEffect(() => {
    const element = button.current;
    element?.addEventListener('click', onPress);
    return () => element?.removeEventListener('click', onPress);
  }, [onPress]);
  return <nldd-button ref={button} appearance="secondary" text={props.text} />;
}

const searchPage = PageBlueprint.make({
  params: {
    path: '/search',
    title: 'Zoeken',
    noHeader: true,
    loader: async () => <SearchPage />,
  },
});

/** Replaces the plugin's page: its lists, filters and empty state are Material and English. */
export const searchModule = createFrontendModule({
  pluginId: 'search',
  extensions: [searchPage],
});
